import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2023 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { AbKeyMap } from "abweb/collection/AbKeyMap";
import { AbSet } from "abweb/collection/AbSet";

/**
 * Utility class for managing events and callbacks. Takes an object mapping keys to callback types, then provides
 * typesafe callback registration and invocation. Most useful when the files in code where events are invoked
 * are different from the files where events are consumed.
 * 
 * example:
 * interface ExampleSubscribeProps {
 *   phaseChange: (phase: string) => void
 *   
 * }
 * 
 * const subscribeStore = new AbSubscribeStore<ExampleSubscribeProps>()
 * 
 * const MyComponent = (): React.ReactNode => {
 *   const [phase, setPhase] = React.useState<string | undefined>();
 *   useOnMount(() => {
 *     return subscribeStore.subscribe("phaseChange", setPhase)
 *   })
 *   return <div>
 *     Current phase: {phase}
 *     <AbButton label={"Change phase"} onClick={() => subscribeStore.invoke("phaseChange", Math.random().toString())}/>
 *   </div>
 * }
 * 
 * 
 */
export class AbSubscribeStore {
  constructor() {
    _defineProperty(this, "callbacks", AbKeyMap.from());
    /**
     * Add a callback to be called when the event is invoked. Returns a function which removes the callback.
     * IMPORTANT: remember to remove your callbacks when unmounting! Works very well with useEffect.
     */
    _defineProperty(this, "subscribe", (event, func) => {
      const eventCallbacks = this.callbacks.get(event);
      if (eventCallbacks === AbKeyMap.NOT_FOUND) {
        this.callbacks.set(event, AbSet.from([]));
        return this.subscribe(event, func);
      }
      eventCallbacks.add(func);
      return () => eventCallbacks.delete(func);
    });
    /**
     * Invoke every callback registered to the given event with the given args.
     */
    _defineProperty(this, "invoke", (event, ...args) => {
      const eventCallbacks = this.callbacks.get(event);
      if (eventCallbacks === AbKeyMap.NOT_FOUND) return;
      eventCallbacks.forEach(cb => cb(...args));
    });
  }
}
//# sourceMappingURL=AbSubscribeStore.js.map
